import axios from "axios";

import { IMaintenanceJob, IMaintenanceJobDto } from "./MaintenanceJobsModels";
import APP_CONFIG from "../app/configuration/AppConfig";
import { TrackerNamesMap } from "../sites/SiteModels";
import { tryGetTrackerName } from "../trackers/TrackerName";

const maintenanceJobTypeLabels: Record<string, string> = {
  TrackerComponentReplacement: "Tracker Component Replacement",
};

const processPhaseLabels: Record<string, string> = {
  Initializing: "Initializing",
  UpdatingMetadata: "Updating Metadata",
  GeneratingConfig: "Generating Config",
  SendingResetCommand: "Sending Reset Command",
  WaitingForReconnection: "Waiting For Reconnection",
  Completed: "Completed",
  Failed: "Failed",
};

const processPhaseProgress: Record<string, number> = {
  Initializing: 5,
  UpdatingMetadata: 20,
  GeneratingConfig: 40,
  SendingResetCommand: 60,
  WaitingForReconnection: 80,
  Completed: 100,
  Failed: 100,
};

const humanizeEnumValue = (value: string, labels: Record<string, string> = {}) =>
  labels[value] ?? value.replace(/([a-z0-9])([A-Z])/g, "$1 $2");

const getReplacementComponents = (job: IMaintenanceJobDto) =>
  job.replacementDetails
    ?.map((replacement) => `${replacement.componentName}: ${replacement.serialNumber}`)
    .join(", ") || undefined;

const getTargetTrackerName = (job: IMaintenanceJobDto, trackerNamesMap: TrackerNamesMap) => {
  const trackerId = job.replacementDetails?.[0]?.trackerId;
  return trackerId === undefined ? undefined : tryGetTrackerName(trackerId, trackerNamesMap);
};

const mapMaintenanceJobDtoToMaintenanceJob = (
  job: IMaintenanceJobDto,
  fallbackIndex: number,
  trackerNamesMap: TrackerNamesMap
): IMaintenanceJob => {
  const phase = job.currentPhase;
  const rowKey = job.id?.trim() || `${job.siteId ?? "maintenance-job"}-${fallbackIndex}`;

  return {
    rowKey,
    id: job.id,
    name: humanizeEnumValue(job.jobType, maintenanceJobTypeLabels),
    status: humanizeEnumValue(phase, processPhaseLabels),
    progressPercentage: processPhaseProgress[phase],
    createdAt: job.createdAtUtc,
    updatedAt: job.updatedAtUtc,
    description: job.errorMessage ?? undefined,
    replacementComponents: getReplacementComponents(job),
    targetTrackerName: getTargetTrackerName(job, trackerNamesMap),
    performedBy: job.performedBy,
  };
};

export async function queryMaintenanceJobs(
  siteId: string,
  trackerNamesMap: TrackerNamesMap = {}
): Promise<IMaintenanceJob[]> {
  const BASE_URL = `${APP_CONFIG.serviceUrls.apiHostUrl}/maintenance/site/${encodeURIComponent(siteId)}/jobs`;
  const config = {
    method: "GET",
    url: BASE_URL,
  };
  const httpResponse = await axios.request<IMaintenanceJobDto[]>(config);
  return httpResponse.data.map((job, index) =>
    mapMaintenanceJobDtoToMaintenanceJob(job, index, trackerNamesMap)
  );
}
